/**
 * TermsAgreementModal — mandatory Terms of Service / Privacy popup.
 *
 * Design:
 *   • Rendered as a plain fixed-position div at z-9999 (NOT via Radix
 *     Dialog) so it can safely overlay other modals (e.g. the SSO
 *     email prompt) without inheriting focus-trap / outside-click
 *     behaviour.
 *   • Header shows title + close button only — no filename, no
 *     browser PDF toolbar (`#toolbar=0&navpanes=0&scrollbar=0`).
 *   • Single scrollbar: the modal body is one `overflow-y-scroll`
 *     wrapper. The PDF iframe is rendered inside at a very generous
 *     fixed pixel height so the PDF plugin never needs its own
 *     scrollbar (multi-page PDFs lay out linearly at `view=FitH`).
 *   • Agree auto-enables when the user scrolls to the bottom of the
 *     wrapper. `onScroll` + a fallback `onWheel` handler cover the
 *     case where the browser fires wheel-only events while the iframe
 *     absorbs scroll deltas.
 *   • Source of truth: `/api/legal/links`. No hardcoded URL/content.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { X, ShieldCheck, AlertCircle, Loader2, CheckCircle2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const SCROLL_THRESHOLD_PX = 40;
// Tall enough to lay out ~15 A4 pages at typical modal width (~700px)
// so the embedded PDF plugin does not need to render its own
// scrollbar. Empirically covers every Terms/Privacy doc we serve.
const IFRAME_HEIGHT_PX = 12000;

export default function TermsAgreementModal({
    open, onClose, onAgree,
    policyType = "terms_of_service",
    authMethod = "email",
    email = null,
    recordAgree = true,
}) {
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadErr, setLoadErr] = useState(null);
    const [scrolledToBottom, setScrolledToBottom] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const scrollRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        setLoadErr(null);
        setScrolledToBottom(false);
        axios
            .get(`${BACKEND_URL}/api/legal/links`)
            .then((r) => {
                const body = r.data?.data || r.data || {};
                const kindKey = policyType === "privacy_policy" ? "privacy" : "terms";
                const m = body[kindKey] || null;
                const label = policyType === "privacy_policy" ? "Privacy Policy" : "Terms of Service";
                if (!m || !m.url) {
                    setLoadErr(`${label} document is not configured. Please contact your administrator.`);
                    setMeta(null);
                } else {
                    // PDF → embed + scroll-to-agree. Non-PDF (hosted /terms
                    // SPA page) → fallback: link out + allow Agree so signup
                    // never dead-ends when no Terms PDF has been uploaded.
                    setMeta(m);
                }
            })
            .catch((err) => setLoadErr(err?.response?.data?.detail || err?.message || "Failed to load document."))
            .finally(() => setLoading(false));
    }, [open, policyType]);

    // Single reusable "reached bottom?" check. Called by both onScroll
    // and onWheel so we still catch bottom-reach even if the iframe
    // absorbs scroll events and the wrapper's scroll listener stalls.
    const checkAtBottom = () => {
        const el = scrollRef.current;
        if (!el) return;
        const atBottom = (el.scrollTop + el.clientHeight) >= (el.scrollHeight - SCROLL_THRESHOLD_PX);
        if (atBottom) setScrolledToBottom(true);
    };

    // Wheel fallback: if the iframe is capturing scroll deltas, the
    // wrapper's `scrollTop` won't advance but the user's intent is
    // clear. We manually nudge the wrapper down on every downward
    // wheel event over the modal body — this both keeps wrapper
    // scroll working across browsers AND lets us fire the bottom
    // check reliably.
    const onWheel = (e) => {
        const el = scrollRef.current;
        if (!el) return;
        // Only synthesise scroll for downward gestures.
        if (e.deltaY && e.deltaY > 0) {
            el.scrollTop = el.scrollTop + e.deltaY;
        } else if (e.deltaY && e.deltaY < 0) {
            el.scrollTop = el.scrollTop + e.deltaY;
        }
        checkAtBottom();
    };

    const doAgree = async () => {
        if (!meta) return;
        setSubmitting(true);
        try {
            if (recordAgree && meta.version_id) {
                await axios.post(`${BACKEND_URL}/api/legal/agree`, {
                    policy_type: policyType,
                    version_id: meta.version_id,
                    email:       email || undefined,
                    auth_method: authMethod,
                    user_agent:  navigator.userAgent,
                });
            }
            onAgree?.(meta.version_id || null, meta);
        } catch (err) {
            setLoadErr(err?.response?.data?.detail || "Could not record your agreement. Please try again.");
        } finally { setSubmitting(false); }
    };

    // Hosted-page fallback (no PDF uploaded): the backend returns a
    // non-PDF link to the in-app /terms (or /privacy) route. We can't
    // reliably embed the SPA inside itself, so we surface a review link
    // and allow the user to Agree directly.
    const isHosted = !!meta && !meta.is_pdf;
    const docHref = meta?.url
        ? (meta.url.startsWith("/api/") ? `${BACKEND_URL}${meta.url}` : meta.url)
        : "#";

    // Clean-render PDF params: hide toolbars, side panes, and the
    // plugin's internal scrollbar. `view=FitH` fits width so the doc
    // reads like an in-app document.
    const pdfSrc = useMemo(() => (
        meta?.url ? `${BACKEND_URL}${meta.url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH` : ""
    ), [meta]);

    if (!open) return null;

    const title = policyType === "privacy_policy" ? "Privacy Policy" : "Terms of Service";

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
             style={{ pointerEvents: "auto" }}
             data-testid="tos-modal-backdrop"
             role="dialog" aria-modal="true" aria-labelledby="tos-modal-title"
             onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
            <div className="bg-background border border-border rounded-lg shadow-2xl w-full max-w-3xl h-[85vh] flex flex-col overflow-hidden"
                 style={{ pointerEvents: "auto" }}
                 data-testid="tos-modal"
                 onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-3 border-b border-border shrink-0">
                    <div className="flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4 text-primary" />
                        <h2 id="tos-modal-title" className="text-sm font-semibold" data-testid="tos-modal-title">
                            {title}
                        </h2>
                    </div>
                    <button onClick={onClose}
                            className="text-muted-foreground hover:text-foreground p-1 rounded"
                            data-testid="tos-modal-close"
                            aria-label="Close">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Body — ONE scrollbar. The wrapper below is the only
                    scrollable surface; the iframe height is fixed at
                    ~12000px so the PDF plugin renders every page in
                    that vertical space without needing its own bar. */}
                <div className="flex-1 overflow-hidden relative bg-secondary/20">
                    {loading && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-muted-foreground">
                            <Loader2 className="w-5 h-5 animate-spin" />
                            <span className="text-xs">Loading document…</span>
                        </div>
                    )}
                    {!loading && loadErr && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-center px-6" data-testid="tos-modal-error">
                            <AlertCircle className="w-6 h-6 text-destructive" />
                            <div className="text-sm text-destructive">{loadErr}</div>
                        </div>
                    )}
                    {!loading && meta && !loadErr && !isHosted && (
                        <div ref={scrollRef}
                             onScroll={checkAtBottom}
                             onWheel={onWheel}
                             tabIndex={0}
                             className="absolute inset-0 overflow-y-scroll overflow-x-hidden focus:outline-none"
                             style={{ WebkitOverflowScrolling: "touch" }}
                             data-testid="tos-modal-scroll">
                            <iframe
                                src={pdfSrc}
                                title={title}
                                data-testid="tos-modal-pdf"
                                className="block w-full border-0 bg-white"
                                style={{ height: `${IFRAME_HEIGHT_PX}px`, pointerEvents: "none" }}
                                tabIndex={-1}
                            />
                            {/* Sentinel so `scrollHeight` includes a tiny
                                buffer past the iframe — makes the "at
                                bottom" test deterministic across
                                browsers. */}
                            <div style={{ height: "24px" }} aria-hidden="true" />
                        </div>
                    )}
                    {!loading && meta && !loadErr && isHosted && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-center px-8" data-testid="tos-modal-hosted">
                            <ShieldCheck className="w-8 h-8 text-primary" />
                            <div className="text-sm text-foreground max-w-sm leading-relaxed">
                                Please review our <span className="font-medium">{title}</span> before continuing.
                            </div>
                            <a href={docHref} target="_blank" rel="noreferrer"
                               className="inline-flex items-center gap-1.5 text-sm text-primary underline hover:text-primary/80"
                               data-testid="tos-modal-hosted-link">
                                Open {title} in a new tab
                            </a>
                            <div className="text-[11px] text-muted-foreground max-w-sm">
                                By clicking Agree you confirm you have read and accept the {title}.
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="border-t border-border px-5 py-3 flex flex-col gap-2 shrink-0 bg-muted/20">
                    <div className="text-[11px] text-muted-foreground flex items-center gap-2 min-h-[18px]">
                        {isHosted ? (
                            <>Review the document above, then click Agree to continue.</>
                        ) : scrolledToBottom ? (
                            <><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> You&apos;ve reached the end.</>
                        ) : meta ? (
                            <>Scroll to the bottom of the document to enable the Agree button.</>
                        ) : null}
                    </div>
                    <div className="flex items-center justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={onClose}
                                data-testid="tos-modal-cancel">
                            Cancel
                        </Button>
                        <Button size="sm" onClick={doAgree}
                                disabled={!meta || (!isHosted && !scrolledToBottom) || submitting}
                                data-testid="tos-modal-agree">
                            {submitting ? (
                                <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Recording…</>
                            ) : (
                                <>Agree</>
                            )}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
